import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Users, CrownSimple, Money, ListChecks, ChatCircleDots, GraduationCap, Ticket, FileText, Plus, WhatsappLogo, Check, X, EnvelopeSimple, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, tone, tid }) {
  return (
    <div data-testid={tid} className="rounded-2xl border border-border bg-white p-5">
      <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: `${tone}1a`, color: tone }}><Icon size={22} weight="duotone" /></div>
      <div className="mt-3 font-display text-2xl font-extrabold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [planId, setPlanId] = useState("monthly");
  const [count, setCount] = useState(5);
  const [reqs, setReqs] = useState([]);
  const [msgs, setMsgs] = useState([]);

  const loadCoupons = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  const loadReqs = () => api.get("/admin/subscription-requests?status=pending").then((r) => setReqs(r.data)).catch(() => {});
  const loadMsgs = () => api.get("/admin/contact").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    loadCoupons();
    loadReqs();
    loadMsgs();
  }, []);

  const approve = async (id) => {
    try { await api.post(`/admin/subscription-requests/${id}/approve`); toast.success("Abonnement activé ✓"); loadReqs(); }
    catch { toast.error("Validation impossible"); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/subscription-requests/${id}/reject`); toast.success("Demande rejetée"); loadReqs(); }
    catch { toast.error("Action impossible"); }
  };

  const generate = async () => {
    try {
      const { data } = await api.post("/admin/coupons", { plan_id: planId, count: Number(count) });
      toast.success(`${data.created.length} code(s) ${data.plan} générés`);
      loadCoupons();
    } catch { toast.error("Génération impossible"); }
  };

  const resolveMsg = async (id) => {
    try { const { data } = await api.post(`/admin/contact/${id}/resolve`); toast.success(data.status === "resolved" ? "Marqué comme traité ✓" : "Rouvert"); loadMsgs(); }
    catch { toast.error("Action impossible"); }
  };
  const deleteMsg = async (id) => {
    try { await api.delete(`/admin/contact/${id}`); toast.success("Message supprimé"); loadMsgs(); }
    catch { toast.error("Suppression impossible"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3 animate-fade-up">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Back-office</span>
          <h1 className="mt-2 font-display text-3xl font-extrabold sm:text-4xl">Administration</h1>
        </div>
        <Link to="/app/admin/documents" data-testid="admin-documents-link">
          <Button variant="outline" className="rounded-full"><FileText className="mr-1.5" /> Documents stratégiques</Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Stat tid="admin-users" icon={Users} label="Utilisateurs" value={stats?.total_users ?? "—"} tone="#FF4B0A" />
        <Stat tid="admin-students" icon={GraduationCap} label="Élèves" value={stats?.students ?? "—"} tone="#042A65" />
        <Stat tid="admin-subs" icon={CrownSimple} label="Abonnements actifs" value={stats?.active_subscriptions ?? "—"} tone="#FFB800" />
        <Stat tid="admin-revenue" icon={Money} label="Revenus (FCFA)" value={(stats?.revenue ?? 0).toLocaleString("fr-FR")} tone="#355070" />
        <Stat tid="admin-quizzes" icon={ListChecks} label="Quiz réalisés" value={stats?.quiz_attempts ?? "—"} tone="#6D597A" />
        <Stat tid="admin-ai" icon={ChatCircleDots} label="Messages IA" value={stats?.ai_messages ?? "—"} tone="#B56576" />
      </div>

      <div className="rounded-2xl border border-border bg-white p-6" data-testid="admin-sub-requests">
        <div className="flex items-center gap-2"><WhatsappLogo weight="fill" className="text-[#25D366]" size={22} /><h2 className="font-display text-xl font-bold">Demandes d'abonnement à valider</h2>
          <span className="ml-2 rounded-full bg-terracotta/10 px-2.5 py-0.5 text-xs font-bold text-terracotta">{reqs.length}</span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Après réception du paiement Mobile Money via WhatsApp (+237 659 946 824), validez la demande pour activer l'accès.</p>
        {reqs.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="no-requests">Aucune demande en attente.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {reqs.map((r) => (
              <div key={r.id} data-testid={`req-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-cream px-4 py-3">
                <div>
                  <div className="font-semibold">{r.user_name} <span className="text-xs text-muted-foreground">· {r.user_email}</span></div>
                  <div className="text-sm text-terracotta">{r.plan_name} — {r.price?.toLocaleString("fr-FR")} FCFA</div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`approve-${r.id}`} onClick={() => approve(r.id)} className="rounded-full bg-forest text-white hover:bg-forest/90"><Check weight="bold" className="mr-1" /> Valider</Button>
                  <Button data-testid={`reject-${r.id}`} onClick={() => reject(r.id)} variant="outline" className="rounded-full border-destructive text-destructive hover:bg-destructive/5"><X weight="bold" className="mr-1" /> Rejeter</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-white p-6" data-testid="admin-contact-messages">
        <div className="flex items-center gap-2"><EnvelopeSimple weight="fill" className="text-terracotta" size={22} /><h2 className="font-display text-xl font-bold">Messages de contact</h2>
          <span className="ml-2 rounded-full bg-terracotta/10 px-2.5 py-0.5 text-xs font-bold text-terracotta">{msgs.filter((m) => m.status !== "resolved").length} nouveau(x)</span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Messages envoyés depuis la page Contact (également transmis via WhatsApp).</p>
        {msgs.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="no-messages">Aucun message pour le moment.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {msgs.map((m) => (
              <div key={m.id} data-testid={`msg-${m.id}`} className={`rounded-xl border px-4 py-3 ${m.status === "resolved" ? "border-border bg-cream/60" : "border-terracotta/30 bg-terracotta/5"}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold">{m.name}
                      {m.email && <span className="text-xs text-muted-foreground"> · {m.email}</span>}
                      {m.status === "resolved" && <span className="ml-2 rounded-full bg-forest/10 px-2 py-0.5 text-[11px] font-bold text-forest">traité</span>}
                    </div>
                    <div className="mt-1 whitespace-pre-wrap text-sm text-ink/80">{m.message}</div>
                    <div className="mt-1 text-[11px] text-muted-foreground">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button data-testid={`resolve-${m.id}`} onClick={() => resolveMsg(m.id)} variant="outline" className="rounded-full">
                      <Check weight="bold" className="mr-1" /> {m.status === "resolved" ? "Rouvrir" : "Traité"}
                    </Button>
                    <Button data-testid={`delete-${m.id}`} onClick={() => deleteMsg(m.id)} variant="outline" className="rounded-full border-destructive text-destructive hover:bg-destructive/5">
                      <Trash weight="bold" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-white p-6">
        <h2 className="mb-4 font-display text-xl font-bold">Utilisateurs récents</h2>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rôle</TableHead>
                <TableHead>XP</TableHead><TableHead>Abonnement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell><span className="rounded-full bg-sand px-2.5 py-0.5 text-xs font-semibold capitalize">{u.role}</span></TableCell>
                  <TableCell>{u.xp ?? 0}</TableCell>
                  <TableCell>{u.subscription ? <span className="text-forest">{u.subscription.plan_name}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-center gap-2"><Ticket weight="fill" className="text-terracotta" size={22} /><h2 className="font-display text-xl font-bold">Codes d'activation / coupons</h2></div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <select data-testid="coupon-plan" value={planId} onChange={(e) => setPlanId(e.target.value)} className="rounded-xl border border-border bg-white px-3 py-2.5 text-sm">
            <option value="daily">Journalier</option><option value="weekly">Hebdomadaire</option>
            <option value="monthly">Mensuel</option><option value="annual">Annuel</option>
          </select>
          <input data-testid="coupon-count" type="number" min="1" max="100" value={count} onChange={(e) => setCount(e.target.value)} className="w-24 rounded-xl border border-border bg-white px-3 py-2.5 text-sm" />
          <Button data-testid="generate-coupons" onClick={generate} className="rounded-full bg-terracotta text-white hover:bg-[#E03E00]"><Plus weight="bold" className="mr-1" /> Générer</Button>
          <span className="text-sm text-muted-foreground">{coupons.length} code(s) au total · {coupons.filter((c) => !c.used).length} disponibles</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {coupons.slice(0, 24).map((c) => (
            <span key={c.code} className={`rounded-lg px-2.5 py-1 font-mono text-xs ${c.used ? "bg-sand text-muted-foreground line-through" : "bg-forest/10 text-forest"}`}>{c.code}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
